import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, Headset, XIcon } from 'lucide-react';
import dayjs from 'dayjs';
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@noor/ui';
import { useMerchantChats, type MerchantChat } from '@/entities/merchant-chat';
import { MerchantConversationList } from './merchant-conversation-list';
import { MerchantChatThread } from './merchant-chat-thread';

/** The drawer's screen: a thread id, the new-chat composer, or the list. */
export type OpenMerchantChat = number | 'new' | null;

/**
 * The chat drawer — the old `ChatDrawer`, ported. List and thread swap inside
 * one 442px sheet (full-bleed on phones); the header swaps with them. Owns the
 * list query so the thread header can resolve its chat from the same cache the
 * rows render from.
 */
export function MerchantChatDrawer({
  open,
  chatId,
  onChatIdChange,
  onClose,
}: {
  open: boolean;
  chatId: OpenMerchantChat;
  onChatIdChange: (chatId: OpenMerchantChat) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const listQuery = useMerchantChats(search, open);

  const chats = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [listQuery.data],
  );
  const openChat: MerchantChat | null =
    typeof chatId === 'number'
      ? (chats.find((chat) => chat.id === chatId) ?? null)
      : null;

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      direction="right"
    >
      <DrawerContent className="data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-[442px]">
        {/* The visual headers below carry their own text; this labels the sheet. */}
        <DrawerTitle className="sr-only">{t('chat.title')}</DrawerTitle>
        <DrawerHeader className="h-[74px] shrink-0 flex-row items-center justify-between gap-3 p-4 text-left">
          {chatId ? (
            <ThreadHeader chat={openChat} onBack={() => onChatIdChange(null)} />
          ) : (
            <>
              <span className="flex items-center gap-2">
                <span className="flex size-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Headset className="size-4" />
                </span>
                <span className="text-2xl leading-7 font-semibold tracking-tight text-foreground">
                  {t('chat.support')}
                </span>
              </span>
              <DrawerClose asChild>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('chat.close')}
                  className="cursor-pointer rounded-full p-1 text-muted-foreground transition-colors outline-none hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40"
                >
                  <XIcon className="size-6" />
                </button>
              </DrawerClose>
            </>
          )}
        </DrawerHeader>
        {chatId ? (
          <MerchantChatThread
            chatId={chatId}
            chat={openChat}
            onCreated={(id) => onChatIdChange(id)}
          />
        ) : (
          <MerchantConversationList
            chats={chats}
            search={search}
            onSearchChange={setSearch}
            isLoading={listQuery.isLoading || listQuery.isFetching}
            hasNextPage={listQuery.hasNextPage}
            onLoadMore={() => void listQuery.fetchNextPage()}
            onNewChat={() => onChatIdChange('new')}
            onSelect={(id) => onChatIdChange(id)}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}

/** Back chevron + peer avatar, name, and last-activity stamp. */
function ThreadHeader({
  chat,
  onBack,
}: {
  chat: MerchantChat | null;
  onBack: () => void;
}) {
  const { t } = useTranslation('common');
  // Hardcoded, as in the source: nameless peers read "New Chat" everywhere.
  const firstName = chat?.user?.first_name ?? 'New';
  const lastName = chat?.user?.last_name ?? 'Chat';
  const updatedAt = chat?.updated_at;
  const stamp =
    updatedAt && dayjs(updatedAt).isValid()
      ? dayjs(updatedAt).format('DD-MM-YYYY HH:mm')
      : dayjs().format('DD-MM-YYYY HH:mm');

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        aria-label={t('actions.back')}
        className="cursor-pointer rounded-full p-1 transition-colors outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40"
      >
        <ChevronLeftIcon className="size-5" />
      </button>
      <span
        aria-hidden="true"
        className="flex size-12 shrink-0 items-center justify-center rounded-[20px] bg-primary text-base font-semibold text-primary-foreground"
      >
        {firstName.charAt(0)}
        {lastName.charAt(0)}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-base leading-[19px] font-semibold tracking-tight text-foreground">
          {firstName} {lastName}
        </span>
        <span className="text-base leading-[19px] font-medium tracking-tight text-muted-foreground">
          {stamp}
        </span>
      </span>
    </div>
  );
}
